import React, { useEffect, useMemo, useState } from "react";
import SidebarLayout from "../../components/SidebarLayout";
import Modal from "../../components/Modal";
import { BACKEND_URL } from "../../config";
import { useAuth } from "../../modules/auth/authContext";

interface Employee {
  id: number;
  name: string;
}

interface LeaveRow {
  id: number;
  employee_id: number;
  leave_type?: string;
  from_date: string;
  to_date: string;
  reason?: string;
  status?: string;
  rejection_reason?: string;
  employee?: Employee;
}

const today = () => new Date().toISOString().slice(0, 10);

const EmployeeLeaves: React.FC = () => {
  const { user } = useAuth();
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [leaves, setLeaves] = useState<LeaveRow[]>([]);
  const [statusFilter, setStatusFilter] = useState("");
  const [loading, setLoading] = useState(false);
  const [modal, setModal] = useState({ open: false, title: "", message: "" });
  const [form, setForm] = useState({
    employee_id: "",
    leave_type: "casual",
    from_date: today(),
    to_date: today(),
    reason: "",
  });

  const employeeName = useMemo(() => {
    const map = new Map<number, string>();
    employees.forEach((employee) => map.set(employee.id, employee.name));
    return map;
  }, [employees]);

  const api = async (path: string, options: RequestInit = {}) => {
    const res = await fetch(`${BACKEND_URL}${path}`, {
      credentials: "include",
      headers: { "Content-Type": "application/json", ...(options.headers || {}) },
      ...options,
    });
    if (!res.ok) throw new Error(await res.text());
    return res.json();
  };

  const loadData = async () => {
    setLoading(true);
    try {
      const [employeeData, leaveData] = await Promise.all([
        api("/api/employees"),
        api(`/api/attendance/leave-requests${statusFilter ? `?status=${statusFilter}` : ""}`),
      ]);
      setEmployees(employeeData);
      setLeaves(leaveData);
    } catch (err: any) {
      setModal({ open: true, title: "Unable to load leaves", message: err.message || "Please try again." });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, [statusFilter]);

  const saveLeave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.employee_id) {
      setModal({ open: true, title: "Missing employee", message: "Select an employee before saving leave." });
      return;
    }

    try {
      await api("/api/attendance/leave-requests", {
        method: "POST",
        body: JSON.stringify({
          ...form,
          employee_id: Number(form.employee_id),
          status: "pending",
        }),
      });
      setForm((prev) => ({ ...prev, employee_id: "", reason: "" }));
      setModal({ open: true, title: "Leave added", message: "Leave request was saved." });
      await loadData();
    } catch (err: any) {
      setModal({ open: true, title: "Unable to save leave", message: err.message || "Please try again." });
    }
  };

  const updateLeaveStatus = async (leave: LeaveRow, status: string) => {
    try {
      await api(`/api/attendance/leave-requests/${leave.id}`, {
        method: "PUT",
        body: JSON.stringify({
          status,
          approved_by_id: user?.employee_id || null,
          approved_at: status === "approved" ? new Date().toISOString() : null,
          rejection_reason: status === "rejected" ? "Rejected by admin" : null,
        }),
      });
      await loadData();
    } catch (err: any) {
      setModal({ open: true, title: "Unable to update leave", message: err.message || "Please try again." });
    }
  };

  return (
    <SidebarLayout title="Leaves" subtitle="Add and approve employee leave requests.">
      <Modal open={modal.open} title={modal.title} message={modal.message} onClose={() => setModal((prev) => ({ ...prev, open: false }))} />

      <div className="grid grid-cols-1 gap-5 xl:grid-cols-[420px_minmax(0,1fr)]">
        <form onSubmit={saveLeave} className="rounded border border-border-light bg-surface-light p-5">
          <h2 className="mb-4 text-xl font-bold">Add Leave</h2>
          <div className="flex flex-col gap-3">
            <label>
              <div className="mb-1 text-sm font-semibold">Employee</div>
              <select required value={form.employee_id} onChange={(e) => setForm((prev) => ({ ...prev, employee_id: e.target.value }))} className="w-full rounded border p-3">
                <option value="">Select employee</option>
                {employees.map((employee) => <option key={employee.id} value={employee.id}>{employee.name}</option>)}
              </select>
            </label>
            <label>
              <div className="mb-1 text-sm font-semibold">Leave Type</div>
              <select value={form.leave_type} onChange={(e) => setForm((prev) => ({ ...prev, leave_type: e.target.value }))} className="w-full rounded border p-3">
                <option value="casual">Casual</option>
                <option value="sick">Sick</option>
                <option value="earned">Earned</option>
                <option value="unpaid">Unpaid</option>
              </select>
            </label>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <label>
                <div className="mb-1 text-sm font-semibold">From</div>
                <input type="date" value={form.from_date} onChange={(e) => setForm((prev) => ({ ...prev, from_date: e.target.value }))} className="w-full rounded border p-3" />
              </label>
              <label>
                <div className="mb-1 text-sm font-semibold">To</div>
                <input type="date" value={form.to_date} onChange={(e) => setForm((prev) => ({ ...prev, to_date: e.target.value }))} className="w-full rounded border p-3" />
              </label>
            </div>
            <label>
              <div className="mb-1 text-sm font-semibold">Reason</div>
              <textarea value={form.reason} onChange={(e) => setForm((prev) => ({ ...prev, reason: e.target.value }))} className="min-h-24 w-full rounded border p-3" />
            </label>
          </div>
          <div className="mt-4 flex justify-end">
            <button type="submit" className="rounded bg-primary px-5 py-2 font-semibold text-white">Save Leave</button>
          </div>
        </form>

        <section className="rounded border border-border-light bg-surface-light p-5">
          <div className="mb-4 flex flex-col justify-between gap-3 sm:flex-row sm:items-center">
            <h2 className="text-xl font-bold">Leave Requests</h2>
            <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="rounded border p-2">
              <option value="">All Status</option>
              <option value="pending">Pending</option>
              <option value="approved">Approved</option>
              <option value="rejected">Rejected</option>
            </select>
          </div>

          {loading ? <div>Loading...</div> : (
            <div className="overflow-x-auto">
              <table className="w-full min-w-[780px] text-left text-sm">
                <thead className="bg-background-light text-xs uppercase text-subtext-light">
                  <tr>
                    <th className="p-2">Employee</th>
                    <th className="p-2">Type</th>
                    <th className="p-2">From</th>
                    <th className="p-2">To</th>
                    <th className="p-2">Status</th>
                    <th className="p-2">Reason</th>
                    <th className="p-2">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {leaves.map((leave) => (
                    <tr key={leave.id} className="border-t">
                      <td className="p-2 font-semibold">{leave.employee?.name || employeeName.get(leave.employee_id) || leave.employee_id}</td>
                      <td className="p-2 capitalize">{leave.leave_type || "-"}</td>
                      <td className="p-2">{leave.from_date}</td>
                      <td className="p-2">{leave.to_date}</td>
                      <td className="p-2 capitalize">{leave.status || "-"}</td>
                      <td className="p-2">{leave.reason || "-"}</td>
                      <td className="p-2">
                        <div className="flex gap-2">
                          <button type="button" onClick={() => updateLeaveStatus(leave, "approved")} className="rounded bg-success-green px-3 py-1 text-xs font-semibold text-white">Approve</button>
                          <button type="button" onClick={() => updateLeaveStatus(leave, "rejected")} className="rounded bg-alert-red px-3 py-1 text-xs font-semibold text-white">Reject</button>
                        </div>
                      </td>
                    </tr>
                  ))}
                  {!leaves.length && <tr><td colSpan={7} className="p-4 text-center text-subtext-light">No leave requests found.</td></tr>}
                </tbody>
              </table>
            </div>
          )}
        </section>
      </div>
    </SidebarLayout>
  );
};

export default EmployeeLeaves;
